import { DocumentNode, useApolloClient } from '@apollo/client';

import { TreatmentSourceType } from 'components/TreatmentProgressMenu/TreatmentMenuConfig';
import { REQUEST_METHODS } from 'constants/index';
import { RESUBMISSION_PENDING } from 'constants/patientCard';
import { TRACK_EVENTS } from 'constants/segment';
import { callApi } from 'services/api/request';
import {
  ATTACH_TREATMENT_DOCUMENTS,
  GET_TREATMENT_FILE_UPLOAD_URL
} from 'services/query/treatmentPlanning';
import { Classification, FilesData } from 'types/Files.types';
import {
  TreatmentDocumentInput,
  TreatmentDocumentRes,
  TreatmentDocumentType,
  TreatmentDocumentUploadInput,
  TreatmentDocumentUploadUrlRes
} from 'types/TreatmentPlanning.types';
import { translate } from 'utils';
import { showToast } from 'utils/toast/toast.util';

interface Props {
  treatmentId: string;
  documentType: TreatmentDocumentType;
  isResubmission?: boolean;
  isImageFlip?: boolean;
  /** When true, files are uploaded to S3 but `attachTreatmentDocuments` is not called. */
  skipAttachTreatmentDocuments?: boolean;
  sourceType: TreatmentSourceType;
  refetchQueries?: Array<DocumentNode>;
  onUploadSuccess?: () => void;
  onUploadFail?: () => void;
  setFiles: React.Dispatch<React.SetStateAction<FilesData[]>>;
  setUploadingFilesCount?: React.Dispatch<React.SetStateAction<number>>;
  query?: {
    url: DocumentNode;
    urlKey: string;
    attach: DocumentNode;
    attachKey?: string;
  };
}
const useFileUpload = ({
  treatmentId,
  documentType,
  sourceType,
  onUploadSuccess,
  onUploadFail,
  refetchQueries,
  setUploadingFilesCount,
  isResubmission = false,
  isImageFlip = false,
  skipAttachTreatmentDocuments = false,
  setFiles,
  query
}: Props) => {
  const client = useApolloClient();
  const getFileUploadUrl = async (
    fileUploadInput: TreatmentDocumentInput[]
  ) => {
    return client.query<
      TreatmentDocumentUploadUrlRes,
      TreatmentDocumentUploadInput
    >({
      query: query?.url ?? GET_TREATMENT_FILE_UPLOAD_URL,
      variables: {
        documents: fileUploadInput,
        treatmentId
      },
      fetchPolicy: 'network-only'
    });
  };

  const uploadFileToS3 = async (
    url: string,
    key: string,
    file: FilesData | File
  ) => {
    if (isImageFlip) {
      const imgBody = new Blob([file as File], { type: documentType });

      return callApi({
        payload: { method: REQUEST_METHODS.PUT, url, data: imgBody },
        headers: { 'Content-Type': '' }
      });
    } else {
      const isFilesData = (file: FilesData | File): file is FilesData =>
        (file as FilesData).id !== undefined;

      const onUploadProgress = (progressEvent: ProgressEvent) => {
        const progress = (progressEvent.loaded * 100) / progressEvent.total;

        setFiles((files) => {
          if (isFilesData(file)) {
            const updatedFile = files.find(({ id }) => id === file.id);
            if (updatedFile) {
              updatedFile.uploadProgress = progress;
              updatedFile.key = key;
              return [...files];
            }
            file.uploadProgress = progress;
            return files.concat(file);
          }
          return files;
        });
      };

      const imgBody = new Blob([(file as FilesData).file ?? (file as File)], {
        type: documentType
      });

      return callApi({
        payload: { method: REQUEST_METHODS.PUT, url, data: imgBody },
        headers: { 'Content-Type': '' },
        onUploadProgress
      });
    }
  };

  const saveUploadedFile = async (
    fileUploadInput: TreatmentDocumentInput[]
  ) => {
    return client.mutate<TreatmentDocumentRes, TreatmentDocumentUploadInput>({
      mutation: query?.attach ?? ATTACH_TREATMENT_DOCUMENTS,
      variables: {
        documents: fileUploadInput,
        treatmentId
      },
      refetchQueries,
      fetchPolicy: 'network-only'
    });
  };

  const updateIdOfUploadedFile = (
    uploadedFile: TreatmentDocumentRes,
    filesInfo: FilesData[]
  ) => {
    setFiles((oldFiles) => {
      const updatedFiles = oldFiles.map((file) => {
        const updatedFile = filesInfo.find(({ id }) => id === file.id);
        if (updatedFile) {
          const attachKey = query?.attachKey ?? 'attachTreatmentDocuments';
          updatedFile.fileId = uploadedFile[attachKey][0].id;
          return updatedFile;
        }
        return file;
      });
      return updatedFiles;
    });
    window?.analytics.track(TRACK_EVENTS.PATIENT_PHOTOS_UPLOADED);
  };

  const uploadFile = async (files: FilesData[], flippedFile?: File) => {
    const fileInfo = [...files];
    const batchIds = new Set(fileInfo.map((f) => f.id));

    const clearFailedUploadBatch = () => {
      setUploadingFilesCount?.(0);
      onUploadFail?.();
      setFiles((current) =>
        current.filter((f) => !batchIds.has(f.id) || !!f.fileId)
      );
    };

    const clearUploadProgressForBatch = () => {
      setFiles((current) =>
        current.map((f) =>
          batchIds.has(f.id) ? { ...f, uploadProgress: undefined } : f
        )
      );
    };

    const fileInputToGetS3Url: TreatmentDocumentInput[] = files?.map((file) => {
      const date = new Date();
      const fileUploadInput: TreatmentDocumentInput = {
        name: `${date.getTime()} ${file.file?.name}` as string, // For handling apollo client issue for multiple GET_TREATMENT_FILE_UPLOAD_URL query with same files.
        sourceType,
        documentType,
        classification:
          documentType === TreatmentDocumentType.CBCT
            ? Classification.CBCT
            : undefined
      };
      if (isResubmission) {
        fileUploadInput.resubmissionStatus = RESUBMISSION_PENDING;
      }
      if (isImageFlip) {
        fileUploadInput.key = file.awsKey;
      }

      return fileUploadInput;
    });
    try {
      const { data: documentUrl } = await getFileUploadUrl(fileInputToGetS3Url);
      const queryKey = query?.urlKey ?? 'getTreatmentDocumentsUploadUrl';
      const uploadFilePromise = documentUrl?.[queryKey].map(
        async (documentUploadUrl, index) => {
          const { url, key } = documentUploadUrl;
          fileInputToGetS3Url[index].key = key;
          const fileToUpload = isImageFlip ? flippedFile : fileInfo[index];
          return await uploadFileToS3(
            url,
            key,
            fileToUpload as FilesData | File
          );
        }
      );
      if (!isImageFlip) {
        Promise.all(uploadFilePromise)
          .then(async () => {
            if (skipAttachTreatmentDocuments) {
              onUploadSuccess?.();
              setUploadingFilesCount?.(0);
              clearUploadProgressForBatch();
            } else {
              const { data } = await saveUploadedFile(fileInputToGetS3Url);
              if (data) {
                onUploadSuccess?.();
                setUploadingFilesCount?.(0);
                updateIdOfUploadedFile(data, files);
              }
            }
            showToast(
              translate('success.multiplePhotosUploaded', { x: files.length }),
              true
            );
          })
          .catch(() => {
            showToast(translate('uploadingStatus.failedToast'), false);
            clearFailedUploadBatch();
          });
        return uploadFilePromise;
      }
    } catch (error) {
      clearFailedUploadBatch();
      throw error;
    }
  };
  return { getFileUploadUrl, uploadFile };
};

export default useFileUpload;
